import { apiFetch } from "@/lib/api";
import type { AdminDashboardDto } from "@/lib/types";
import { useSession } from "./session-provider";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface AdminDashboardContextValue {
  data: AdminDashboardDto | null;
  pendingAccessRequestCount: number;
  loading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

const AdminDashboardContext =
  createContext<AdminDashboardContextValue | null>(null);

export function useAdminDashboard(): AdminDashboardContextValue {
  const value = useContext(AdminDashboardContext);
  if (!value) {
    throw new Error(
      "useAdminDashboard must be used within AdminDashboardProvider",
    );
  }
  return value;
}

export function AdminDashboardProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { isAdmin } = useSession();
  const [data, setData] = useState<AdminDashboardDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async (showLoading: boolean) => {
    if (!isAdmin) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (showLoading) {
      setLoading(true);
      setError(null);
    }
    try {
      setData(await apiFetch<AdminDashboardDto>("/api/admin/dashboard"));
      setError(null);
    } catch (nextError) {
      if (showLoading) setError(nextError);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [isAdmin]);

  const refetch = useCallback(() => refresh(true), [refresh]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!isAdmin) return;
    const refreshOnFocus = () => void refresh(false);
    window.addEventListener("focus", refreshOnFocus);
    return () => window.removeEventListener("focus", refreshOnFocus);
  }, [isAdmin, refresh]);

  const value = useMemo(
    () => ({
      data,
      pendingAccessRequestCount: data?.pendingAccessRequestCount ?? 0,
      loading: loading || (isAdmin && data === null && error === null),
      error,
      refetch,
    }),
    [data, error, isAdmin, loading, refetch],
  );

  return (
    <AdminDashboardContext.Provider value={value}>
      {children}
    </AdminDashboardContext.Provider>
  );
}
